/**
 * Defers loading of tri-hero slideshow images that aren't the first visible
 * slide in each lane. Native `loading="lazy"` doesn't reliably defer images
 * that sit inside a horizontally-scrolling carousel track (they're still
 * positioned in normal document flow, just clipped), so this does it
 * explicitly via IntersectionObserver, scoped to each slideshow's own
 * scroll container.
 *
 * Progressive enhancement: if JS fails or IntersectionObserver isn't
 * available, images are hydrated immediately so nothing stays blank.
 */
(function () {
  function hydrate(img) {
    if (!img || !img.dataset || !img.dataset.src) return;

    var picture = img.closest('picture');
    if (picture) {
      var source = picture.querySelector('source[data-srcset]');
      if (source) {
        source.setAttribute('srcset', source.dataset.srcset);
        source.removeAttribute('data-srcset');
      }
    }

    if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
    if (img.dataset.sizes) img.setAttribute('sizes', img.dataset.sizes);
    img.setAttribute('src', img.dataset.src);

    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-sizes');
    img.classList.remove('tri-hero-slide__img--lazy');
  }

  function init() {
    var lazyImages = document.querySelectorAll('.tri-hero-slide__img--lazy[data-src]');
    if (!lazyImages.length) return;

    if (!('IntersectionObserver' in window)) {
      lazyImages.forEach(hydrate);
      return;
    }

    var scrollerGroups = new Map();
    lazyImages.forEach(function (img) {
      var scroller = img.closest('slideshow-slides') || null;
      if (!scrollerGroups.has(scroller)) scrollerGroups.set(scroller, []);
      scrollerGroups.get(scroller).push(img);
    });

    scrollerGroups.forEach(function (imgs, scroller) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var target = entry.target;
              observer.unobserve(target);
              // Defer the actual attribute swap off the critical path so it
              // doesn't compete with rendering/scripting work already in flight.
              if ('requestIdleCallback' in window) {
                requestIdleCallback(function () {
                  hydrate(target);
                });
              } else {
                setTimeout(function () {
                  hydrate(target);
                }, 0);
              }
            }
          });
        },
        {
          root: scroller,
          // Only start loading once a slide is actually about to be scrolled
          // into view - a large margin here just loads everything upfront,
          // which defeats the point for 2-slide lanes.
          rootMargin: '0px 24px 0px 24px',
          threshold: 0.01,
        }
      );
      imgs.forEach(function (img) {
        observer.observe(img);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();